/**
 * OnboardingBasicPage
 * 
 * Step 1 of onboarding wizard - collects jurisdiction state.
 * 
 * User selects:
 * - State: U.S. state where case is filed
 * 
 * State is stored locally, navigates to /onboarding/confirm on submit.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthWizardLayout } from '@/layout/AuthWizardLayout';

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
  'District of Columbia', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota',
  'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey',
  'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon',
  'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah',
  'Vermont', 'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
];

interface OnboardingFormData {
  state: string | null;
}

export function OnboardingBasicPage() {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState<OnboardingFormData>({
    state: null,
  });

  const [error, setError] = useState<string | null>(null);

  const handleStateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormData({ state: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset error
    setError(null);
    
    if (!formData.state) {
      setError('Please select your state.');
      return;
    }

    // Store form data in sessionStorage (temporary)
    sessionStorage.setItem('onboarding_data', JSON.stringify(formData));

    // Navigate to confirmation page
    navigate('/onboarding/confirm');
  };

  const canSubmit = !!formData.state;

  return (
    <AuthWizardLayout
      title="Start your setup"
      subtitle="First, tell us where your case is filed."
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* State Selection */}
        <div>
          <label htmlFor="state" className="block text-sm font-medium text-slate-200 mb-2">
            Select Your State
          </label>
          <select
            id="state"
            value={formData.state || ''}
            onChange={handleStateChange}
            className="w-full px-4 py-2 bg-[#1a2332] border border-slate-600 text-white rounded-lg focus:ring-2 focus:ring-[#C2410C] focus:border-[#C2410C]"
          >
            <option value="">Select a state...</option>
            {US_STATES.map((state) => (
              <option key={state} value={state}>
                {state}
              </option>
            ))}
          </select>
          <p className="mt-2 text-xs text-slate-400">
            This helps us show the right forms and deadlines later. You can change this if needed.
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="p-3 bg-red-900/20 border border-red-500/50 rounded-lg text-sm text-red-200">
            {error}
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={!canSubmit}
          className="w-full py-3 px-4 bg-[#C2410C] text-white font-semibold rounded-lg shadow-md hover:bg-[#A0330A] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Continue
        </button>
      </form>
    </AuthWizardLayout>
  );
}
